import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { RRule } from 'rrule';

import { apiJson, ApiError } from '@/lib/api';
import type { CalendarEvent, HouseholdResponse } from '@/lib/types';

type RecurrencePreset =
  | 'none'
  | 'daily'
  | 'weekly'
  | 'weekdays'
  | 'biweekly'
  | 'monthly'
  | 'yearly';

type AddEventSearch = {
  date?: string;
  event_id?: string;
};

function toIcalUtcTimestamp(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

function buildRrule(
  preset: RecurrencePreset,
  startAt: Date,
  recurrenceEndDate?: string
): string | null {
  if (preset === 'none') return null;
  const weekdayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  const byDay = weekdayMap[startAt.getDay()];
  let baseRule: string | null = null;
  switch (preset) {
    case 'daily':
      baseRule = 'FREQ=DAILY';
      break;
    case 'weekly':
      baseRule = `FREQ=WEEKLY;BYDAY=${byDay}`;
      break;
    case 'weekdays':
      baseRule = 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
      break;
    case 'biweekly':
      baseRule = `FREQ=WEEKLY;INTERVAL=2;BYDAY=${byDay}`;
      break;
    case 'monthly':
      baseRule = `FREQ=MONTHLY;BYMONTHDAY=${startAt.getDate()}`;
      break;
    case 'yearly':
      baseRule = 'FREQ=YEARLY';
      break;
    default:
      return null;
  }

  if (!baseRule || !recurrenceEndDate) return baseRule;

  const recurrenceEnd = new Date(`${recurrenceEndDate}T00:00:00`);
  recurrenceEnd.setHours(
    startAt.getHours(),
    startAt.getMinutes(),
    startAt.getSeconds(),
    0
  );

  return `${baseRule};UNTIL=${toIcalUtcTimestamp(recurrenceEnd)}`;
}

function AddCalendarEventPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [startAt, setStartAt] = useState(
    search.date ? `${search.date}T12:00` : new Date().toISOString().slice(0, 16)
  );
  const [endAt, setEndAt] = useState(
    new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)
  );
  const [recurrence, setRecurrence] = useState<RecurrencePreset>('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [selectedBeneficiaryIds, setSelectedBeneficiaryIds] = useState<number[]>([]);
  const [responsibleUserId, setResponsibleUserId] = useState<number | null>(null);
  const [reminderMinutesBefore, setReminderMinutesBefore] = useState<number | null>(
    null
  );

  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  const firstHousehold = householdsQuery.data?.[0];
  const recurrenceRule = useMemo(
    () => buildRrule(recurrence, new Date(startAt), recurrenceEndDate || undefined),
    [recurrence, recurrenceEndDate, startAt]
  );

  const preview = useMemo(() => {
    if (!recurrenceRule) return [];
    try {
      const parsed = RRule.fromString(recurrenceRule);
      const rule = new RRule({ ...parsed.origOptions, dtstart: new Date(startAt) });
      return rule.all((_date: Date, i: number) => i < 5).map((d: Date) =>
        d.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      );
    } catch {
      return [];
    }
  }, [recurrenceRule, startAt]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (
        recurrence !== 'none' &&
        recurrenceEndDate &&
        new Date(`${recurrenceEndDate}T23:59:59`) < new Date(startAt)
      ) {
        throw new Error('Recurrence end date must be on or after the event start date.');
      }

      return apiJson<CalendarEvent>(getToken, '/v1/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          location: location || null,
          notes: notes || null,
          start_at: new Date(startAt).toISOString(),
          end_at: isAllDay ? null : new Date(endAt).toISOString(),
          is_all_day: isAllDay,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          beneficiary_ids: selectedBeneficiaryIds,
          responsible_user_id: responsibleUserId,
          rrule: recurrenceRule,
          reminder_minutes_before: reminderMinutesBefore,
        }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Could not save event.';
      alert(message);
    },
  });

  const toggleBeneficiary = (beneficiaryId: number) => {
    setSelectedBeneficiaryIds((prev) =>
      prev.includes(beneficiaryId)
        ? prev.filter((value) => value !== beneficiaryId)
        : [...prev, beneficiaryId]
    );
  };

  return (
    <div className='min-h-screen bg-sky-light'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
          <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
            <FiArrowLeft /> Back to Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <form
          className='bg-white rounded-2xl border border-gray-200 p-5 space-y-4'
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <h1 className='text-xl font-bold text-gray-900'>Add Event</h1>

          <label className='block'>
            <span className='text-sm font-semibold text-gray-700'>Title</span>
            <input
              className='mt-1 w-full rounded-xl border border-gray-300 px-3 py-2'
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </label>

          <label className='block'>
            <span className='text-sm font-semibold text-gray-700'>Location</span>
            <input
              className='mt-1 w-full rounded-xl border border-gray-300 px-3 py-2'
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
          </label>

          <label className='block'>
            <span className='text-sm font-semibold text-gray-700'>Notes</span>
            <textarea
              className='mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 min-h-20'
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          <label className='inline-flex items-center gap-2 text-sm font-semibold text-gray-700'>
            <input
              type='checkbox'
              checked={isAllDay}
              onChange={(event) => setIsAllDay(event.target.checked)}
            />
            All day event
          </label>

          <div className='grid sm:grid-cols-2 gap-3'>
            <label className='block'>
              <span className='text-sm font-semibold text-gray-700'>Start</span>
              <input
                type='datetime-local'
                className='mt-1 w-full rounded-xl border border-gray-300 px-3 py-2'
                value={startAt}
                onChange={(event) => setStartAt(event.target.value)}
                required
              />
            </label>
            {!isAllDay && (
              <label className='block'>
                <span className='text-sm font-semibold text-gray-700'>End</span>
                <input
                  type='datetime-local'
                  className='mt-1 w-full rounded-xl border border-gray-300 px-3 py-2'
                  value={endAt}
                  onChange={(event) => setEndAt(event.target.value)}
                />
              </label>
            )}
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-1'>Recurrence</p>
            <div className='flex flex-wrap gap-2'>
              {(
                [
                  ['none', 'None'],
                  ['daily', 'Daily'],
                  ['weekly', 'Weekly'],
                  ['weekdays', 'Weekdays'],
                  ['biweekly', 'Biweekly'],
                  ['monthly', 'Monthly'],
                  ['yearly', 'Yearly'],
                ] as [RecurrencePreset, string][]
              ).map(([value, label]) => (
                <button
                  type='button'
                  key={value}
                  onClick={() => setRecurrence(value)}
                  className={`rounded-full px-3 py-1 text-sm border ${
                    recurrence === value
                      ? 'bg-primary/10 border-primary text-primary font-semibold'
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {recurrence !== 'none' && (
              <label className='block mt-3'>
                <span className='text-sm font-semibold text-gray-700'>
                  Repeat until (optional)
                </span>
                <input
                  type='date'
                  className='mt-1 w-full rounded-xl border border-gray-300 px-3 py-2'
                  value={recurrenceEndDate}
                  min={startAt.slice(0, 10)}
                  onChange={(event) => setRecurrenceEndDate(event.target.value)}
                />
              </label>
            )}
            {preview.length > 0 && (
              <div className='mt-2 text-xs text-gray-600'>
                <p className='font-semibold text-gray-700'>Next 5 occurrences</p>
                {preview.map((value: string) => (
                  <p key={value}>{value}</p>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-1'>Children (optional)</p>
            <div className='flex flex-wrap gap-2'>
              {(firstHousehold?.beneficiaries ?? []).map((beneficiary) => (
                <button
                  key={beneficiary.beneficiary_id}
                  type='button'
                  onClick={() => toggleBeneficiary(beneficiary.beneficiary_id)}
                  className={`rounded-full px-3 py-1 text-sm border ${
                    selectedBeneficiaryIds.includes(beneficiary.beneficiary_id)
                      ? 'bg-primary/10 border-primary text-primary font-semibold'
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                >
                  {beneficiary.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-1'>Responsible parent</p>
            <select
              className='w-full rounded-xl border border-gray-300 px-3 py-2'
              value={responsibleUserId ?? ''}
              onChange={(event) =>
                setResponsibleUserId(event.target.value ? Number(event.target.value) : null)
              }
            >
              <option value=''>Both</option>
              {(firstHousehold?.members ?? []).map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.first_name ?? `User ${member.user_id}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <p className='text-sm font-semibold text-gray-700 mb-1'>Reminder</p>
            <select
              className='w-full rounded-xl border border-gray-300 px-3 py-2'
              value={reminderMinutesBefore ?? ''}
              onChange={(event) =>
                setReminderMinutesBefore(
                  event.target.value ? Number(event.target.value) : null
                )
              }
            >
              <option value=''>None</option>
              <option value='15'>15 min</option>
              <option value='60'>1 hour</option>
              <option value='180'>3 hours</option>
              <option value='1440'>1 day</option>
            </select>
          </div>

          <button
            type='submit'
            disabled={mutation.isPending}
            className='w-full rounded-xl bg-primary text-white font-semibold py-3 hover:bg-primary-hover transition-colors disabled:opacity-70'
          >
            {mutation.isPending ? 'Saving...' : 'Save Event'}
          </button>
        </form>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/events/add')({
  component: AddCalendarEventPage,
  validateSearch: (search: Record<string, unknown>): AddEventSearch => {
    const validated: AddEventSearch = {};

    if (typeof search.date === 'string') {
      validated.date = search.date;
    }
    if (typeof search.event_id === 'string') {
      validated.event_id = search.event_id;
    }

    return validated;
  },
});
